import { useMutation, useQueryClient } from '@tanstack/react-query';
import { hostQueryKeys, Hosts } from '#api';
import { makeHostEffectiveAccessQueryOptions, makeHostQueryOptions } from '#api/hosts.queries.ts';
import { maxTagsQueryOptions } from '#api/tags.queries.ts';
import type { Host } from '#dn-types/host.ts';

export function useUpdateHostCache() {
  const queryClient = useQueryClient();

  return async function updateHostCache(host: Host) {
    // Invalidate all lists, since there's new host information
    void queryClient.invalidateQueries({ queryKey: hostQueryKeys.lists() });

    // If the host has tags, check to see if we need to invalidate the max tags query (a new one has been added)
    if (host.tags.length) {
      const allTags = queryClient.getQueryData(maxTagsQueryOptions.queryKey)?.data;
      if (allTags) {
        const allTagNames = allTags.map((t) => t.name);
        for (const hostTag of host.tags) {
          if (!allTagNames.includes(hostTag)) {
            await queryClient.invalidateQueries(maxTagsQueryOptions);
            break;
          }
        }
      }
    }

    // Set the returned host into our query cache
    queryClient.setQueryData(makeHostQueryOptions(host.id).queryKey, { data: host });

    // Outbound access is computed server-side from the host's role and tags, so an edit to either
    // leaves the cached entry describing the old host. The detail page edits tags in place without
    // remounting, so nothing else would refetch it. Targeted at the exact key: a detail(id) prefix
    // would also re-GET the host just written above. Not awaited, so the outbound table refetches
    // in the background instead of holding the tag editor open.
    void queryClient.invalidateQueries(makeHostEffectiveAccessQueryOptions(host.id));
  };
}

export function useAddHost() {
  return useMutation({ mutationFn: Hosts.addHost });
}

export function useEditHost() {
  return useMutation({ mutationFn: Hosts.editHost });
}
